import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

import { SitecoreMyBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { StylesLazyLoadingService } from '@frontend/sports/deferred/feature/styles-loader';
import { firstValueFrom } from 'rxjs';

import { MyBetsBetSlip } from '../../../my-bets-base/models/my-bets-viewmodels';
import { MyBetsDateRangeFilterContstraint, MyBetsFilterConstraint, MyBetsStateFilterContstraint } from '../../models/my-bets-filter-constraints';
import { MyBetsBetslipRepository } from '../../services/my-bets-betslip.repository';

export interface MyBetsBetslipsResponse {
    hasErrors?: boolean;
    betslips: MyBetsBetSlip[];
}

@Injectable()
export class MyBetsPrintResolver {
    filterConstraint = new MyBetsFilterConstraint();

    constructor(
        private repository: MyBetsBetslipRepository,
        private stylesLazyLoadingService: StylesLazyLoadingService,
        private sitecoreMyBets: SitecoreMyBetsConfig,
    ) {}

    async resolve(route: ActivatedRouteSnapshot): Promise<MyBetsBetslipsResponse> {
        const { betNumber, betState, fromDate, toDate, associatedAccount } = route.queryParams;
        const stylesPromise = this.stylesLazyLoadingService.lazyLoad('print');
        const myBetsPromise = firstValueFrom(this.sitecoreMyBets.whenReady);
        if (betNumber) {
            const [singleResponse] = await Promise.all([
                this.repository.getSingleBetslip(betNumber, associatedAccount),
                stylesPromise,
                myBetsPromise,
            ]);

            return {
                hasErrors: singleResponse.errorLoadingBets,
                betslips: singleResponse.betslips,
            };
        }

        if (betState) {
            const stateConstraint = new MyBetsStateFilterContstraint('stateConstraint', betState);
            this.filterConstraint.addConstraint(stateConstraint);
        }

        if (fromDate && toDate) {
            const rangeConstraint = new MyBetsDateRangeFilterContstraint('dateRangeConstraint', new Date(fromDate), new Date(toDate));
            this.filterConstraint.addConstraint(rangeConstraint);
        }

        if (associatedAccount) {
            this.filterConstraint.addConstraint(this.repository.getBeforeFilter(associatedAccount));
        }

        const listOptions = {
            filterConstraint: this.filterConstraint,
        };
        const [listResponse] = await Promise.all([this.repository.getAllBetslips(listOptions), stylesPromise, myBetsPromise]);

        return {
            hasErrors: listResponse.errorLoadingBets,
            betslips: listResponse.betslips,
        };
    }
}
